/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 删除按钮组件（使用新的 Redux Slice）
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-04-12 09:37:31
 */
import React, { useCallback, useMemo } from 'react'
import { Button, Modal } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import { trackPicsPageClickV2 } from '../../utils/track'
import CONFIG from '../../../../constants/config'
import { deletePhoto, deletePhotosV2 } from '../../../../actions/photo'
import { capturePromiseException } from '../../../../utils/sentryUtils'
import { buildFetchPhotosParamsWithOptions } from '../../lib/photo'
import {
  PhotoSelectionSource,
  useActiveSelection,
  buildSuggestedPhotosQueryFilter
} from '../../utils/selectionHelpers'
import { selectSearchFilter } from '../../../../slices/photoFilterSlice'
import { fetchPhotosV2 } from '../../../../slices/photosByContentSlice'
import { fetchPhotosByPrecise } from '../../../../slices/photosByPreciseSlice'
import type { RootState } from '../../../../app/store'

interface DeleteButtonProps {
  dataSource?: PhotoSelectionSource
  onDeleteSuccess?: () => void
}

const DeleteButton: React.FC<DeleteButtonProps> = ({
  dataSource = 'content',
  onDeleteSuccess
}) => {
  const dispatch = useDispatch()
  const {
    selectionConfig,
    hasSuggestedSelected,
    activePhotos: photos,
    activePhotosNum: photosNum,
    activeCheckAll: checkAll,
    activeCheckedPhotos: checkedPhotos,
    activeUnCheckedPhotos: unCheckedPhotos,
    suggestedWords,
    suggestedQueryFilter
  } = useActiveSelection(dataSource)
  const queryFilter = useSelector(selectSearchFilter)
  const groupID = useSelector((state: RootState) => state.group.groupId)
  const myRole = useSelector((state: RootState) => state.group.myRole)
  const isAscSort = useSelector((state: RootState) => {
    if (dataSource === 'precise') {
      return state.photosByPrecise.isAscSort
    }
    return state.photosByContent.isAscSort
  })

  const isShowButton = checkedPhotos.size > 0 || checkAll

  // 计算选中的照片数量
  const calcCheckedPhotoNum = useMemo(() => {
    if (checkAll) {
      return photosNum - unCheckedPhotos.size
    }
    return checkedPhotos.size
  }, [checkAll, photosNum, unCheckedPhotos.size, checkedPhotos.size])

  // 当前已加载且本次勾选中的照片，是否包含「已下载」标签（未加载页不参与判断）
  const hasDownloadedTagInCurrentPhotos = useMemo(() => {
    const selectedLoaded = checkAll
      ? photos.filter((photo: any) => !unCheckedPhotos.has(photo.seq))
      : photos.filter((photo: any) => checkedPhotos.has(photo.seq))
    return selectedLoaded.some((photo: any) => Boolean(photo?.isDownloaded))
  }, [photos, checkAll, checkedPhotos, unCheckedPhotos])

  const onDeletePhotos = useCallback(() => {
    sensors.track('web_click_pop_delete_photo', {
      clickItem: 'done',
      role: sensorsRole(myRole),
      bool2: hasDownloadedTagInCurrentPhotos,
      photoNum: calcCheckedPhotoNum
    })

    let currentQueryFilter = queryFilter
    if (hasSuggestedSelected && currentQueryFilter) {
      currentQueryFilter =
        buildSuggestedPhotosQueryFilter(
          queryFilter,
          suggestedQueryFilter,
          suggestedWords || []
        ) || currentQueryFilter
    }

    if (checkAll) {
      // 全选删除：使用 deletePhotosV2 和 filter
      if (!currentQueryFilter) {
        console.error('queryFilter 不存在，无法执行全选删除')
        return
      }

      const plainQueryFilter = {
        ...currentQueryFilter,
        times:
          currentQueryFilter.times &&
          currentQueryFilter.times[0] &&
          currentQueryFilter.times[1]
            ? [
                {
                  start: currentQueryFilter.times[0].format(CONFIG.timeFormat),
                  end: currentQueryFilter.times[1].format(CONFIG.timeFormat)
                }
              ]
            : [],
        users:
          currentQueryFilter.departmentUsers?.map((user) => user.userID) || [],
        departmentIDList:
          currentQueryFilter.departments?.map((department) => department.id) ||
          [],
        watermarks:
          currentQueryFilter.watermarks?.map((watermark) => {
            const parts = watermark.split('+')
            return {
              watermarkBaseID: parts[0] || '',
              groupWatermarkID: parts[1] || ''
            }
          }) || []
      }

      dispatch(
        deletePhotosV2({
          deleteFilter: {
            filter: plainQueryFilter,
            exclude: Array.from(unCheckedPhotos).map((seq) => {
              const photo = photos.find((p) => p.seq === seq)
              return photo?.file || ''
            })
          },
          groupID: groupID
        }) as any
      )
        .then((data: any) => {
          sensors.track('web_workgroup_picspage_delete_pics', {
            photosNum: calcCheckedPhotoNum,
            isDeleteSuc: data.status === 0,
            role: CONFIG.saKeywords.roles[myRole]
          })

          if (data.status === -206) {
            sensors.track(
              'web_show_pop_workgroup_download_delete_photo_limit_exceeded',
              {
                operationType: 'delete',
                photoNum: calcCheckedPhotoNum,
                groupID: groupID,
                role: sensorsRole(myRole)
              }
            )
          }

          if (data.status !== 0) {
            return
          }

          if (hasSuggestedSelected) {
            dispatch({ type: 'photosByContent/clearSuggestedCheck' })
          } else {
            dispatch(selectionConfig.clearCheckAction())
          }

          const refreshParams = buildFetchPhotosParamsWithOptions({
            queryFilter: currentQueryFilter as any,
            groupID: groupID,
            isPreciseSearch: dataSource === 'precise',
            isAscSort: isAscSort,
            lastPageCond: ''
          })

          if (dataSource === 'precise') {
            dispatch(fetchPhotosByPrecise(refreshParams) as any)
          } else if (hasSuggestedSelected) {
            dispatch(
              fetchPhotosV2({
                ...refreshParams,
                queryFilter: currentQueryFilter
              } as any)
            )
          } else {
            dispatch(fetchPhotosV2(refreshParams) as any)
          }

          if (onDeleteSuccess) {
            onDeleteSuccess()
          }
        })
        .catch(capturePromiseException)
    } else {
      // 部分删除：使用 deletePhoto
      if (!currentQueryFilter) {
        console.error('queryFilter 不存在，无法执行部分删除')
        return
      }

      dispatch(
        deletePhoto({
          group_id: groupID,
          photo_list: Array.from(checkedPhotos)
        }) as any
      )
        .then((data: any) => {
          sensors.track('web_workgroup_picspage_delete_pics', {
            photosNum: checkedPhotos.size,
            isDeleteSuc: data.status === 0,
            role: CONFIG.saKeywords.roles[myRole]
          })

          if (data.status !== 0) {
            return
          }

          if (hasSuggestedSelected) {
            dispatch({ type: 'photosByContent/clearSuggestedCheck' })
          } else {
            dispatch(selectionConfig.clearCheckAction())
          }

          const params = buildFetchPhotosParamsWithOptions({
            queryFilter: currentQueryFilter as any,
            groupID: groupID,
            isPreciseSearch: dataSource === 'precise',
            isAscSort: isAscSort,
            lastPageCond: ''
          })

          if (dataSource === 'precise') {
            dispatch(fetchPhotosByPrecise(params) as any)
          } else {
            if (hasSuggestedSelected) {
              dispatch(
                fetchPhotosV2({
                  ...params,
                  queryFilter: currentQueryFilter
                } as any)
              )
            } else {
              dispatch(fetchPhotosV2(params) as any)
            }
          }

          if (onDeleteSuccess) {
            onDeleteSuccess()
          }
        })
        .catch(capturePromiseException)
    }
  }, [
    checkAll,
    queryFilter,
    suggestedQueryFilter,
    suggestedWords,
    hasSuggestedSelected,
    selectionConfig,
    groupID,
    unCheckedPhotos,
    checkedPhotos,
    photos,
    myRole,
    calcCheckedPhotoNum,
    dataSource,
    isAscSort,
    dispatch,
    onDeleteSuccess,
    hasDownloadedTagInCurrentPhotos
  ])

  const onDeletePhotoClick = useCallback(() => {
    trackPicsPageClickV2({
      clickItem: 'delete'
    })

    Modal.confirm({
      title: '确定删除选中照片？',
      content: '照片删除后不支持找回，所在文件夹内照片也会被删除，请谨慎操作。',
      cancelText: '取消',
      okText: '确定',
      onOk: onDeletePhotos,
      onCancel: () => {
        sensors.track('web_click_pop_delete_photo', {
          clickItem: 'cancel',
          role: sensorsRole(myRole),
          bool2: hasDownloadedTagInCurrentPhotos,
          photoNum: calcCheckedPhotoNum
        })
      }
    })
  }, [
    onDeletePhotos,
    myRole,
    groupID,
    hasDownloadedTagInCurrentPhotos,
    calcCheckedPhotoNum
  ])

  if (isShowButton) {
    return (
      <Button
        type="link"
        onClick={onDeletePhotoClick}
        style={{ marginRight: 10 }}
      >
        删除
      </Button>
    )
  }
  return null
}

export default DeleteButton
